import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { Trans } from '@lingui/macro';
import dayjs from 'dayjs';

import styled from 'styled-components';
import {
  DayPickerInputWrapper,
  TextFieldWrapper,
} from '@lib/final-form-oca-ui';
import { FileUploaderField } from '@features/common';
import { dateTokens } from '@lib/i18n';
import { DocumentCard, RequiredTag, OptionalTag } from './document-card';

const datePickerOptions = {
  formatDate: date => dayjs(date).format('L'),
};

const BANK_NAME_MAX = 255;

// Bank loan documents require a bank name and an expiry date (backend rule).
const requiredText = value =>
  value && String(value).trim() ? (
    undefined
  ) : (
    <Trans>This is required field</Trans>
  );
const bankNameRules = value =>
  requiredText(value) ||
  (value.trim().length > BANK_NAME_MAX ? (
    <Trans>Bank name must be at most 255 characters</Trans>
  ) : (
    undefined
  ));

/**
 * Single document entry (passport, civil ID, visa, work permission, bank loan).
 * Rendered inside a DocumentCard so every entry has the same shell.
 */
export function FieldsetDocument({
  name,
  title,
  withUid,
  isOptional,
  isBankLoan,
  onRemove,
}) {
  return (
    <DocumentCard
      title={title}
      tag={isOptional ? <OptionalTag /> : <RequiredTag />}
      onRemove={onRemove}
    >
      {isBankLoan ? (
        <>
          <FormGrid>
            <FormCol>
              <Field
                name={`${name}.bankName`}
                component={TextFieldWrapper}
                label={<Trans>Bank name</Trans>}
                maxLength={BANK_NAME_MAX}
                validate={bankNameRules}
                required
              />
            </FormCol>
            <FormCol>
              <Field
                name={`${name}.uid`}
                component={TextFieldWrapper}
                label={<Trans>Reference</Trans>}
              />
            </FormCol>
            <FormCol>
              <Field
                name={`${name}.expDate`}
                label={<Trans>Expire date</Trans>}
                validate={requiredText}
                parse={val =>
                  val ? dayjs(val).format(dateTokens.ISO_DATE) : ''
                }
                format={val => (val ? new Date(val).toISOString() : null)}
                component={DayPickerInputWrapper}
                options={datePickerOptions}
                required
              />
            </FormCol>
          </FormGrid>
          <FormGrid style={{ marginTop: 10 }}>
            <FormCol span={3}>
              <FileUploaderField
                label={<Trans>Picture / scan</Trans>}
                name={`${name}.files`}
              />
            </FormCol>
          </FormGrid>
        </>
      ) : (
        <FormGrid>
          {withUid && (
            <FormCol>
              <Field
                name={`${name}.uid`}
                component={TextFieldWrapper}
                label={<Trans>Number</Trans>}
              />
            </FormCol>
          )}
          <FormCol span={withUid ? 1 : 1}>
            <Field
              name={`${name}.expDate`}
              label={<Trans>Expire date</Trans>}
              parse={val => (val ? dayjs(val).format(dateTokens.ISO_DATE) : '')}
              format={val => (val ? new Date(val).toISOString() : null)}
              component={DayPickerInputWrapper}
              options={datePickerOptions}
            />
          </FormCol>
          <FormCol span={withUid ? 1 : 2}>
            <FileUploaderField
              label={<Trans>Picture / scan</Trans>}
              name={`${name}.files`}
            />
          </FormCol>
        </FormGrid>
      )}
    </DocumentCard>
  );
}

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormCol = styled.div`
  width: 100%;
  min-width: 0;
  ${({ span }) => span && `grid-column: span ${span};`}
`;

FieldsetDocument.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  withUid: PropTypes.bool.isRequired,
  isOptional: PropTypes.bool,
  isBankLoan: PropTypes.bool,
  onRemove: PropTypes.func,
};

FieldsetDocument.defaultProps = {
  isOptional: false,
  isBankLoan: false,
  onRemove: undefined,
};
